<template>
  <div
    v-if="!loading"
    class="dashboard"
    :class="{ 'is-centered': emptyStatistics }"
  >
    <div
      v-if="emptyStatistics"
      class="box is-flex is-centered is-empty"
    >
      <BIcon
        icon="clipboard-alert-outline"
        size="is-large"
        type="is-primary"
        custom-size="mdi-128px"
      />

      <h2 class="title is-2 has-text-primary">
        Aún no has facturado
      </h2>

      <RouterLink
        class="button is-primary is-text"
        :to="{ name: 'NewInvoice' }"
      >
        Haz click aquí para comenzar a facturar.
      </RouterLink>

      <p>ó</p>

      <RouterLink
        class="button is-primary is-text"
        :to="{ name: 'NewSaleNote' }"
      >
        Haz click aquí para crear nota de remisión
      </RouterLink>
    </div>

    <template
      v-else
    >
      <div class="columns is-multiline">
        <div class="column is-12-mobile is-4">
          <div class="box">
            <h3 class="indicator title is-4 title-dashboard-debt">
              <div>Total de deuda:</div>
              <div class="has-text-weight-bold has-text-debt-total">
                {{ totalDebt | toCurrency('MXN') }}
              </div>
            </h3>
          </div>
        </div>
        <div class="column is-12-mobile is-4">
          <div class="box">
            <h3 class="indicator title is-4 title title-dashboard-debt-expired">
              <div>Total Vencido:</div>
              <div class="has-text-weight-bold has-text-debt-expired">
                {{ totalDebtExpired | toCurrency('MXN') }}
              </div>
            </h3>
          </div>
        </div>
        <div class="column is-12-mobile is-4">
          <div class="box">
            <h3 class="indicator title is-4 title-dashboard-debt-overcome">
              <div>Por Vencer:</div>
              <div class="has-text-weight-bold has-text-debt">
                {{ totalDebt - totalDebtExpired | toCurrency('MXN') }}
              </div>
            </h3>
          </div>
        </div>

        <div class="column is-12-mobile is-6">
          <div class="box">
            <apex-chart
              type="bar"
              class="debts-summary"
              :options="barChartOptionsDebtsSummary"
              :series="seriesDebtsSummary"
            />
          </div>
        </div>

        <div class="column is-12-mobile is-6">
          <div class="box">
            <apex-chart
              width="400"
              type="pie"
              :options="chartOptions"
              :series="[totalDebtExpired, totalDebt]"
            />
          </div>
        </div>

        <div class="column is-12-mobile is-4">
          <StatisticsSalesYear />
        </div>

        <div class="column is-12-mobile is-4">
          <StatisticsSalesMonth />
        </div>

        <div class="column is-12-mobile is-4">
          <StatisticsSalesDay />
        </div>

        <div class="column is-6">
          <StatisticsSalesItems />
        </div>

        <div class="column is-6">
          <StatisticsSalesItemsAll />
        </div>
      </div>

      <div
        v-if="bottomUrlRedirect"
        class="is-centered"
      >
        <a
          :href="bottomUrlRedirect"
          target="__blank"
        >
          <img
            :src="bottomBannerImg"
          >
        </a>
      </div>
    </template>
  </div>
</template>

<script>
import merge from 'lodash/merge';
import { createNamespacedHelpers } from 'vuex';

const { mapState, mapGetters, mapActions } = createNamespacedHelpers('statistics');

export default {
  name: 'Dashboard',
  components: {
    StatisticsSalesYear: () => import(/* webpackChunkName: "dashboard-statistics-sales-year" */ '@/components/Admin/Dashboard/StatisticsSalesYear.vue'),
    StatisticsSalesMonth: () => import(/* webpackChunkName: "dashboard-statistics-sales-month" */ '@/components/Admin/Dashboard/StatisticsSalesMonth.vue'),
    StatisticsSalesDay: () => import(/* webpackChunkName: "dashboard-statistics-sales-day" */ '@/components/Admin/Dashboard/StatisticsSalesDay.vue'),
    StatisticsSalesItems: () => import(/* webpackChunkName: "dashboard-statistics-sales-items" */ '@/components/Admin/Dashboard/StatisticsSalesItems.vue'),
    StatisticsSalesItemsAll: () => import(/* webpackChunkName: "dashboard-statistics-sales-items-all" */ '@/components/Admin/Dashboard/StatisticsSalesItemsAll.vue'),
  },
  data() {
    return {
      loading: false,
      bottomUrlRedirect: '',
      bottomBannerImg: '',
      chartOptions: {
        colors: ['#FF495C', '#3DDC97'],
        labels: ['Total Vencido', 'Total Por Vencer'],
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              width: 200,
            },
            legend: {
              position: 'bottom',
            },
          },
        }],
      },
      barChartOptionsDebtsSummary: {
        colors: ['#FF495C', '#3DDC97'],
        xaxis: {
          categories: [
            '46 días o más',
            '31-45 días',
            '16-30 días',
            '1-15 días',
            '1-15 días',
            '16-30 días',
            '31-45 días',
            '46 días o más',
          ],
        },
        dataLabels: {
          enabled: false,
        },
        style: {
          fontSize: '12px',
          colors: ['#FF495C', '#008FFB'],
        },
        plotOptions: {
          bar: {
            horizontal: false,
            barHeight: '80%',
          },
        },
        title: {
          text: 'Deudas Vencidas y Por Vencer',
        },
        grid: {
          xaxis: {
            showLines: false,
          },
        },
        fill: {
          colors: ['#FF495C', '#3DDC97'],
        },
      },
    };
  },
  computed: merge(
    mapState({
      salesAll: ({ salesAll }) => salesAll,
      salesAllNonFiscal: ({ salesAllNonFiscal }) => salesAllNonFiscal,
      totalRows: ({ totalRows }) => totalRows,
      totalDebt: ({ totalDebt }) => totalDebt,
      totalDebtExpired: ({ totalDebtExpired }) => totalDebtExpired,
      fifteenDaysExpiredTotal: ({ fifteenDaysExpiredTotal }) => fifteenDaysExpiredTotal,
      thirtyDaysExpiredTotal: ({ thirtyDaysExpiredTotal }) => thirtyDaysExpiredTotal,
      fortyFiveDaysExpiredTotal: ({ fortyFiveDaysExpiredTotal }) => fortyFiveDaysExpiredTotal,
      sixtyDaysExpiredTotal: ({ sixtyDaysExpiredTotal }) => sixtyDaysExpiredTotal,
      fifteenDaysToExpiredTotal: ({ fifteenDaysToExpiredTotal }) => fifteenDaysToExpiredTotal,
      thirtyDaysToExpiredTotal: ({ thirtyDaysToExpiredTotal }) => thirtyDaysToExpiredTotal,
      fortyFiveDaysToExpiredTotal: ({ fortyFiveDaysToExpiredTotal }) => fortyFiveDaysToExpiredTotal,
      sixtyDaysToExpiredTotal: ({ sixtyDaysToExpiredTotal }) => sixtyDaysToExpiredTotal,
    }),
    mapGetters(['emptyStatistics']),
    {
      seriesDebtsSummary() {
        return [
          {
            name: 'Vencidas',
            data: [
              this.sixtyDaysExpiredTotal,
              this.fortyFiveDaysExpiredTotal,
              this.thirtyDaysExpiredTotal,
              this.fifteenDaysExpiredTotal,
              0,
              0,
              0,
              0,
            ],
          },
          {
            name: 'Por Vencer',
            data: [
              0,
              0,
              0,
              0,
              this.fifteenDaysToExpiredTotal,
              this.thirtyDaysToExpiredTotal,
              this.fortyFiveDaysToExpiredTotal,
              this.sixtyDaysToExpiredTotal,
            ],
          },
        ];
      },
    },
  ),
  async mounted() {
    this.loading = true;

    await Promise.all([
      this.getSales(),
      this.getDebtsSummary(),
    ]);

    this.loading = false;

    const bottomBanner = await this.getBanner('bottom');

    if (bottomBanner.payload) {
      this.bottomBannerImg = bottomBanner.payload.image;
      this.bottomUrlRedirect = bottomBanner.payload.url;
    }
  },
  methods: mapActions(['getBanner', 'getSales', 'getDebtsSummary']),
};
</script>

<style scoped>
.dashboard.is-centered {
  width: 100%;
  height: 100%;
}

.box:not(.is-empty) {
  height: 100%;
}

.box.is-empty {
  flex-direction: column;
}

.columns {
  margin: 0;
}

h2.title.is-2 {
  padding-top: 1rem;
}

.icon.is-large {
  height: 6rem;
  width: 8rem;
}

.indicator {
  justify-content: space-between;
  margin: 0;
}

.indicator > div:first-child {
  padding-bottom: 1rem;
}
</style>

<style>
.mdi-128px {
  font-size: 128px;
}
</style>
